const FOCUSABLE_SELECTOR =
  'input:not(:disabled), select:not(:disabled), textarea:not(:disabled), button:not(:disabled)';
const COLLAPSED_ANCESTOR_SELECTOR = '[class*="--collapsed"]';
const RETRY_INTERVAL_MS = 300;
const MAX_ATTEMPTS = 10;

/**
 * Container presenters (an array/group's own root element, not a field) set
 * this attribute to opt out of auto-focus. Their first focusable descendant is
 * whatever `GroupTitle` renders first — the delete button, then the collapse
 * toggle — never the field the provider was sent to, so focusing it would
 * silently park keyboard focus on a destructive or collapsing control instead.
 */
const SCROLL_ONLY_ATTRIBUTE = 'data-jump-scroll-only';

function focusAndScroll(el: HTMLElement): void {
  const collapsedAncestor = el.closest(COLLAPSED_ANCESTOR_SELECTOR);

  // Content inside a collapsed group/array item is only CSS-clipped, not
  // unmounted. Scroll to the container's header instead of forcing it open —
  // auto-expanding shared presenters was deliberately out of scope.
  if (collapsedAncestor instanceof HTMLElement) {
    collapsedAncestor.scrollIntoView({ behavior: 'smooth', block: 'center' });
    return;
  }

  el.scrollIntoView({ behavior: 'smooth', block: 'center' });

  if (el.hasAttribute(SCROLL_ONLY_ATTRIBUTE)) {
    return;
  }

  const focusable = el.matches(FOCUSABLE_SELECTOR)
    ? el
    : el.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);

  focusable?.focus();
}

/** Scrolls to and focuses a field by its DOM id, retrying while it mounts. */
export function jumpToField(name: string): void {
  let attempts = 0;

  const tryJump = () => {
    attempts += 1;
    const el = document.getElementById(name);

    if (!el) {
      if (attempts < MAX_ATTEMPTS) {
        setTimeout(tryJump, RETRY_INTERVAL_MS);
      }
      return;
    }

    focusAndScroll(el);
  };

  tryJump();
}
